import React from 'react';
import Img from 'gatsby-image';
import { Arrow } from 'components/Icons';
import { Carousel } from 'components/UI/Carousel';
import { Container } from 'components/UI/Container/Container';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import { PrismicLink } from 'components/PrismicLink';
import { PrismicHTMLParser } from 'components/PrismicHTMLParser';
import { Heading } from 'components/LANDR/Heading';
import {
    PrismicRichTextFieldType,
    PrismicTitleFieldType,
    PrismicLinkFieldType,
    PrismicDateTextFieldType,
    FixedGatsbyImageFieldType,
    SliceContext,
} from '../types';
import './Blog.scss';

type BlogItem = {
    blog_post_title: PrismicTitleFieldType;
    blog_post_image: FixedGatsbyImageFieldType;
    blog_post_category: PrismicRichTextFieldType;
    blog_post_date: PrismicDateTextFieldType;
    blog_post_description: PrismicRichTextFieldType;
    blog_post_link: PrismicLinkFieldType;
    blog_post_alt_text: PrismicRichTextFieldType;
};

type BlogSlice = {
    slice_type: string;
    primary: {
        blog_title: PrismicTitleFieldType;
        blog_link_text: PrismicRichTextFieldType;
        blog_link_url: PrismicLinkFieldType;
    };
    items: BlogItem[];
};

type BlogProps = {
    slice: BlogSlice;
    sliceContext: SliceContext;
};

class Blog extends React.Component<BlogProps, {}> {
    renderBlogTitle = () => {
        if (
            this.props.slice.primary.blog_title.raw &&
            this.props.slice.primary.blog_title.raw[0].text
        ) {
            return (
                <Heading
                    className="Blog-title"
                    size="xl"
                    element={this.props.slice.primary.blog_title.raw[0].type}
                >
                    {this.props.slice.primary.blog_title.raw[0].text}
                </Heading>
            );
        }
    };

    renderBlogLink = () => {
        if (
            this.props.slice.primary.blog_link_url &&
            this.props.slice.primary.blog_link_text.text
        ) {
            return (
                <div className="Blog-linkContainer">
                    <PrismicLink
                        className="Blog-link"
                        link={this.props.slice.primary.blog_link_url}
                        slice={this.props.sliceContext}
                    >
                        {this.props.slice.primary.blog_link_text.text}
                        <Arrow className="Blog-linkArrow" color="dark" />
                    </PrismicLink>
                </div>
            );
        } else {
            return null;
        }
    };

    renderBlogPosts = () => {
        const post = (item: BlogItem, index: number) =>
            item.blog_post_link &&
            item.blog_post_image.localFile &&
            item.blog_post_date &&
            item.blog_post_title.raw &&
            item.blog_post_title.raw[0].text &&
            item.blog_post_description.html && (
                <PrismicLink
                    className="BlogPost"
                    link={item.blog_post_link}
                    key={index}
                    slice={this.props.sliceContext}
                >
                    {item.blog_post_image.localFile.childImageSharp && (
                        <Img
                            className="BlogPost-image"
                            fixed={item.blog_post_image.localFile.childImageSharp.fixed}
                            alt={(item.blog_post_alt_text && item.blog_post_alt_text.text) || ''}
                        />
                    )}
                    <div className="BlogPost-meta">
                        {item.blog_post_category.text && (
                            <span className="BlogPost-category">
                                {item.blog_post_category.text}
                            </span>
                        )}
                        {item.blog_post_date && (
                            <time className="BlogPost-date" dateTime={item.blog_post_date}>
                                {item.blog_post_date}
                            </time>
                        )}
                        {item.blog_post_title.raw && item.blog_post_title.raw[0].text && (
                            <Heading
                                className="BlogPost-title"
                                size="lg"
                                element={item.blog_post_title.raw[0].type}
                            >
                                {item.blog_post_title.raw[0].text}
                            </Heading>
                        )}
                        {item.blog_post_description.html && (
                            <PrismicHTMLParser className="BlogPost-description">
                                {item.blog_post_description.html}
                            </PrismicHTMLParser>
                        )}
                    </div>
                </PrismicLink>
            );

        const posts = (view: 'mobile' | 'desktop') => {
            return this.props.slice.items.map((item, index) => {
                if (view === 'desktop') {
                    return (
                        <GridCol
                            colSize={`4`}
                            tabletColSize={'6'}
                            mobileColSize={'12'}
                            offsetSize={`0`}
                            className="BlogPost-cell"
                            key={index}
                        >
                            {post(item, index)}
                        </GridCol>
                    );
                } else if (view === 'mobile') {
                    {
                        return post(item, index);
                    }
                }
            });
        };

        if (this.props.slice.items.length > 0) {
            return (
                <>
                    {/* Mobile Carousel View */}
                    <Carousel className="Blog-posts  Blog-posts--mobile" theme="light">
                        {posts('mobile')}
                    </Carousel>
                    {/* Desktop Grid View */}
                    <div className="Blog-posts--desktop">{posts('desktop')}</div>
                </>
            );
        }
    };

    render() {
        return (
            <div className="Blog">
                <Container className="Blog-container" size="md">
                    <Grid>
                        <GridRow
                            horizontalAlign={'h-start'}
                            verticalAlign={'v-end'}
                            className="Top-elements"
                        >
                            <GridCol colSize={`6`} mobileColSize={'12'} offsetSize={`0`}>
                                {this.renderBlogTitle()}
                            </GridCol>
                            <GridCol colSize={`6`} mobileColSize={'12'} offsetSize={`0`}>
                                {this.renderBlogLink()}
                            </GridCol>
                        </GridRow>
                        <GridRow horizontalAlign={'h-start'} verticalAlign={'v-stretch'}>
                            {this.renderBlogPosts()}
                        </GridRow>
                    </Grid>
                </Container>
            </div>
        );
    }
}

export { Blog, BlogProps, BlogSlice, BlogItem };
